import truncate from 'lodash-es/truncate';
import { Verified } from '@hungryroot/icons';
import { Heading, Paragraph } from '@hungryroot/ramen';
import styles from './CustomerReviewCard.module.css';

type UserInfoProps = {
  background?: string;
  imgSrc?: string;
  name: string;
  location: string;
  bio: string;
};
type TagsProps = {
  tags: string[];
};
type TestimonialProps = {
  className?: string;
  reviewCallOut: string;
  review: string;
  maxCallOutShortChars?: number;
  maxCallOutLongChars?: number;
} & (
  | {
      maxReviewChars?: number;
      truncateReview?: never;
    }
  | {
      truncateReview: false;
      maxReviewChars?: never;
    }
);

type ReviewCardProps = UserInfoProps &
  TagsProps &
  Pick<TestimonialProps, 'reviewCallOut' | 'review'> & {
    id?: string;
    className?: string;
    testimonialContainerClassName?: string;
    contentClassName?: string;
    verifiedText?: string;
  } & (
    | {
        truncateReview?: never;
        maxReviewChars?: number;
      }
    | {
        truncateReview: false;
        maxReviewChars?: never;
      }
  );

const UserInfo = (props: UserInfoProps) => {
  const { background, imgSrc, name, location, bio } = props;
  return (
    <div
      className={
        background ? styles['user-info-background'] : styles['user-info']
      }
    >
      {imgSrc && (
        <img
          src={imgSrc}
          className={
            background ? styles['user-img-background'] : styles['user-img']
          }
          height={80}
          width={80}
          alt={name}
        />
      )}
      <div
        className={
          background ? styles['user-text-background'] : styles['user-text']
        }
      >
        <Heading variant="H6">
          {name} from {location}
        </Heading>
        <Paragraph className={styles['user-bio']}>{bio}</Paragraph>
      </div>
    </div>
  );
};

const Tags = (props: TagsProps) => {
  const { tags } = props;
  return (
    <div className={styles.tags}>
      {tags.map((tag) => {
        return (
          <div key={tag} className={styles.tag}>
            <Paragraph variant="caption" key={tag}>
              {tag}
            </Paragraph>
          </div>
        );
      })}
    </div>
  );
};

const Testimonial = (props: TestimonialProps) => {
  const {
    reviewCallOut,
    review,
    className,
    maxCallOutShortChars = 36,
    maxCallOutLongChars = 103,
    maxReviewChars = 224,
    truncateReview,
  } = props;
  const heading = truncate(reviewCallOut, {
    length: review ? maxCallOutShortChars : maxCallOutLongChars,
    separator: ' ',
  });
  return (
    <div className={`${styles.testimonial} ${className}`}>
      <Heading variant="H3">{heading}</Heading>
      <Paragraph variant="body" className={styles.review}>
        {truncateReview === false
          ? review
          : truncate(review, { length: maxReviewChars, separator: ' ' })}
      </Paragraph>
    </div>
  );
};

export const CustomerReviewCard = (props: ReviewCardProps) => {
  const {
    review,
    reviewCallOut,
    name,
    location,
    bio,
    imgSrc,
    verifiedText,
    tags,
    background,
    testimonialContainerClassName,
    className: classNameProp,
    contentClassName: contentClassNameProp,
    truncateReview,
    maxReviewChars,
  } = props;

  let className = styles.container;
  if (classNameProp !== undefined) {
    className = [className, classNameProp].join(' ');
  }

  let contentClassName =
    background !== undefined
      ? styles['review-content-background']
      : styles['review-content'];
  if (contentClassNameProp !== undefined) {
    contentClassName = [contentClassName, contentClassNameProp].join(' ');
  }

  const testimonialProps: TestimonialProps = {
    className: testimonialContainerClassName,
    reviewCallOut: reviewCallOut,
    review: review,
    truncateReview: truncateReview === false ? truncateReview : undefined,
  };

  if (truncateReview !== false) {
    testimonialProps.maxReviewChars = maxReviewChars;
  }

  return (
    <article className={className}>
      {background && (
        <img
          alt=""
          height={240}
          width={395}
          className={styles.background}
          src={background}
        />
      )}
      <div className={contentClassName}>
        <UserInfo
          background={background}
          imgSrc={imgSrc}
          name={name}
          location={location}
          bio={bio}
        />
        <Tags tags={tags} />
        <Testimonial {...testimonialProps} />
        {verifiedText && (
          <div className={styles.verified}>
            <Verified />
            <Paragraph variant="caption">{verifiedText}</Paragraph>
          </div>
        )}
      </div>
    </article>
  );
};
